<template>
  <div class="alert alert-danger alert-dismissible fade show" role="alert" v-if="Object.keys(orgErrors).length > 0">
    Please review the information entered.
    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
  </div>
  <form ref="orgForm" @submit.prevent>
    <div class="row">
      <div class="col-4">
        <div class="row">
          <div class="col-12 mb-4">
            <label class="form-label required" for="org-create-name">{{ $t('Organization Name') }}</label>
            <input
                name="name"
                id="org-create-name"
                :placeholder="$t('Organization Name')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors.name }"
                @input="e => org.name = e.target.value"
                v-model=org.name

            />
            <div class="invalid-feedback" v-if="orgErrors.name">
              <span v-for="error in orgErrors.name" :key="error">{{ error }}</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12 mb-4">
            <label class="form-label required" for="orgType">{{ $t('Organization Type') }}</label>
            <select
                name="orgType"
                id="orgType"
                class="form-select"
                :class="{ 'is-invalid': orgErrors.type_id }"
                v-model="syncedTypeId"
                @input="handleOrgTypeChange"

            >
              <option value="" selected></option>
              <option v-for="{id, is_clinical, type} in orgTypes" :key="id" :value="id"
                      :data-is-clinical="is_clinical">{{
                  type
                }}
              </option>
            </select>
            <div class="invalid-feedback" v-if="orgErrors.type_id">
              <span v-for="error in orgErrors.type_id" :key="error">{{ error }}</span>
            </div>
          </div>

          <div class="col-12 mb-4" v-if="showClinicalField">
            <label class="form-label required" for="clinicalType">{{ $t('Clinical Type') }}</label>
            <select
                name="clinicalType"
                id="clinicalType"
                class="form-select"
                :class="{ 'is-invalid': orgErrors.clinical_type_id }"
                @input="e => org.clinical_type_id = parseInt(e.target.value)"
            >
              <option value="" selected></option>
              <option v-for="clinicalType in orgClinicalTypes" :key="clinicalType.id"
                      :value="clinicalType.id">
                {{ clinicalType.type }}
              </option>
            </select>
            <div class="invalid-feedback" v-if="orgErrors.clinical_type_id">
              <span v-for="error in orgErrors.clinical_type_id" :key="error">{{ error }}</span>
            </div>
          </div>
          <div class="col-12 mb-4 text-center">
            <a href="#" @click.prevent="resetOrg" class="btn text-end">{{ $t('clear form') }}</a>
          </div>
        </div>
      </div>
      <div class="col-8">
        <div class="row">
          <!-- Start Migration Section -->
          <a href="#" v-if="isEquipmentProvider" class="text-end" key="migrateButton"
             @click.prevent="handleMigrateClick">{{ $t('Migrate Organization') }}</a>
          <!-- End Migration Section -->

          <div class="col-12 mb-4">
            <label class="form-label required" for="location-name">{{ $t('Primary Location Name') }}</label>
            <input
                name="location-name"
                id="location-name"
                :placeholder="$t('Name')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.name'] }"
                @input="e => org.locations[0].name = e.target.value"
                v-model=org.locations[0].name
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.name']">
              <span v-for="error in orgErrors['locations.0.name']" :key="error">{{ error }}</span>
            </div>
          </div>

          <div class="col-12 mb-4">
            <label class="form-label required" for="location-address1">{{ $t('Primary Location Address 1') }}</label>
            <input
                name="location-address1"
                id="location-address1"
                :placeholder="$t('Address 1')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.address1'] }"
                @input="e => org.locations[0].address1 = e.target.value"
                v-model=org.locations[0].address1
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.address1']">
              <span v-for="error in orgErrors['locations.0.address1']" :key="error">{{ error }}</span>
            </div>
          </div>

          <div class="col-12 mb-4">
            <label class="form-label" for="location-address">{{ $t('Primary Location Address 2') }}</label>
            <input
                name="location-address2"
                id="location-address2"
                :placeholder="$t('Address 2')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.address2'] }"
                @input="e => org.locations[0].address2 = e.target.value"
                v-model=org.locations[0].address2
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.address2']">
              <span v-for="error in orgErrors['locations.0.address2']" :key="error">{{ error }}</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6 mb-4">
            <label class="form-label required" for="location-city">{{ $t('Primary Location City') }}</label>
            <input
                name="location-city"
                id="location-city"
                :placeholder="$t('City')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.city'] }"
                @input="e => org.locations[0].city = e.target.value"
                v-model=org.locations[0].city
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.city']">
              <span v-for="error in orgErrors['locations.0.city']" :key="error">{{ error }}</span>
            </div>
          </div>
          <div class="col-6 mb-4">
            <label class="form-label required" for="location-state">{{ $t('Primary Location State') }}</label>
              <AppSelect
                  :options="stateOptions"
                  name="location-state"
                  id="location-state"
                  class="form-select"
                  :class="{ 'is-invalid': orgErrors['locations.0.state'] }"
                  v-model="org.locations[0].state"
                  :placeholder="$t('State')"
              />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.state']">
              <span v-for="error in orgErrors['locations.0.state']" :key="error">{{ error }}</span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6 mb-4">
            <label class="form-label required" for="location-zip">{{ $t('Primary Location ZIP') }}</label>
            <input
                name="location-zip"
                id="location-zip"
                :placeholder="$t('ZIP code')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.zip'] }"
                @input="e => org.locations[0].zip = e.target.value"
                v-model=org.locations[0].zip
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.zip']">
              <span v-for="error in orgErrors['locations.0.zip']" :key="error">{{ error }}</span>
            </div>
          </div>

          <div class="col-6 mb-4">
            <label class="form-label" for="location-phone">{{ $t('Phone') }}</label>
            <VueTelInput type="tel" mode="auto" :valid-characters-only="true"
                         :class="orgErrors['locations.0.phone'] ? 'is-invalid': ''"
                         :only-countries="['US','PR']"
                         :input-options="{ styleClasses: ['form-control', orgErrors['locations.0.phone'] ? 'is-invalid': ''], placeholder: $t('Phone'), name: 'location-phone', id: 'location-phone'}"
                         v-model="org.locations[0].phone" :value="org.locations[0].phone"
                         @input="handlePhoneChange"></VueTelInput>
            <div class="invalid-feedback" v-if="orgErrors['locations.0.phone']">
              <span v-for="error in orgErrors['locations.0.phone']" :key="error">{{ error }}</span>
            </div>
          </div>
        </div>
        <input type="hidden" name="dme-id" id="dme-id" v-model=org.dme_id>
        <div class="row">
          <div class="col-6 mb-4">
            <label class="form-label" for="location-custom-id">{{ $t('Primary Location Custom ID') }}</label>
            <input
                name="location-custom-id"
                id="location-custom-id"
                :placeholder="$t('Custom ID')"
                class="form-control"
                :class="{ 'is-invalid': orgErrors['locations.0.custom_id'] }"
                @input="e => org.locations[0].custom_id = e.target.value"
            />
            <div class="invalid-feedback" v-if="orgErrors['locations.0.custom_id']">
              <span v-for="error in orgErrors['locations.0.custom_id']" :key="error">{{ error }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

  </form>
  <OrgCreateMigratePatients
      v-if="isMigrateClicked" :org="org" @reset-new-location-data="resetOrg"/>

</template>


<script setup>
import {ref, onMounted, defineProps, defineEmits, computed} from 'vue';
import axios from 'axios';
import {VueTelInput} from 'vue3-tel-input'
import 'vue3-tel-input/dist/vue3-tel-input.css'
import OrgCreateMigratePatients from "./migrations/OrgCreateMigratePatients.vue";
import usePermissions from "@/vue/composables/usePermissions";
import AppSelect from "@/vue/shared/v2/AppSelect.vue";
import {stateOptions} from "@/utilities/stateOptions";

const emit = defineEmits([
  'organization-created',
  'create-organization-error',
  'saving-updated',
  'toggle-to-migrate',
  'reset-new-location-data',
  'toggle-patient-migrate',
  'send-patient-data']);
const props = defineProps(
    {
      newLocationData: {
        type: Object,
        required: false,
        default: () => {
          return {};
        }
      }
    }
);

const org = ref({
  draft: true,
  name: null,
  dme_id: null,
  org_type: null,
  type_id: null,
  clinical_type_id: null,
  migrated_at: null,
  migrated_by: null,
  locations: [{
    name: null,
    address1: null,
    address2: null,
    zip: null,
    city: null,
    state: null,
    country: null,
    phone: null
  }]
});

const resetOrg = () => {
  org.value = {
    draft: true,
    name: null,
    dme_id: null,
    org_type: null,
    type_id: null,
    clinical_type_id: null,
    migrated_at: null,
    migrated_by: null,
    locations: [{
      name: null,
      address1: null,
      address2: null,
      zip: null,
      city: null,
      state: null,
      country: null,
      phone: null
    }]
  };
};

const isLoadingState = ref(false);
const showClinicalField = ref(false);
const orgErrors = ref({});
const isEquipmentProvider = ref(false);
const ep_id = ref(0);
const isMigrateClicked = ref(false);
const orgTypes = ref([]);
const orgClinicalTypes = ref([]);
const activePatients = ref([]);
const inactivePatients = ref([]);
const {hasPermission} = usePermissions();
const handleOrgTypeChange = (e) => {
  const selectedTypeId = parseInt(e.target.value);
  org.value.type_id = selectedTypeId;
  showClinicalField.value = isClinicalOrganization(selectedTypeId);
  checkEquipmentProvider(selectedTypeId);
};

const syncedTypeId = computed({
  get: () => org.value.type_id,
  set: (newValue) => {
    org.value.type_id = newValue;
    showClinicalField.value = isClinicalOrganization(newValue);
  }
});

const validatePhoneNumber = (phone) => {
  const countryCodePattern = /^\+1/;
  const rawNumber = phone.replace(countryCodePattern, '').replace(/\D/g, '');

  const truncatedNumber = rawNumber.length > 10 ? rawNumber.substring(0, 10) : rawNumber;

  let formattedNumber = '';
  if (truncatedNumber.length >= 1) {
    formattedNumber = '(' + truncatedNumber.substring(0, 3);
  }
  if (truncatedNumber.length >= 4) {
    formattedNumber += ') ' + truncatedNumber.substring(3, 6);
  }
  if (truncatedNumber.length >= 7) {
    formattedNumber += '-' + truncatedNumber.substring(6, 10);
  }

  org.value.locations[0].phone = formattedNumber;

  const regex = /^\(\d{3}\) \d{3}-\d{4}$/;
  if (!regex.test(org.value.locations[0].phone)) {
    console.error('invalid phone format');
  }
}
const handlePhoneChange = (e) => {
    if (typeof e === 'object' && e.hasOwnProperty('isTrusted') && e.isTrusted === true) {
      const digitsOnly = e.target.value.replace(/\D/g, '');
      if (digitsOnly.length > 10) {
        e.target.value = e.target.value.slice(0, e.target.value.length - (digitsOnly.length - 10));
      }
      validatePhoneNumber(e.target.value);
    }
};

const checkEquipmentProvider = (typeId) => {
  isEquipmentProvider.value = typeId === ep_id.value;
};

const handleMigrateClick = () => {
  isMigrateClicked.value = true;
  emit('toggle-to-migrate');
};

const sendActivePatientsToParent = () => {
  emit('send-patient-data', activePatients.value);
};

const isClinicalOrganization = (typeId) => {
  const types = orgTypes.value;
  const type = types.find(type => type.id === typeId);

  if (type) {
    return type.is_clinical;
  } else {
    return false;
  }
};
const fetchMigrationPatients = async (dme_id) => {
  try {
    const response = await axios.get(`/api/migration-patients/${dme_id}`);
    activePatients.value = JSON.parse(JSON.stringify(response.data.activePatients));
    inactivePatients.value = JSON.parse(JSON.stringify(response.data.inactivePatients));
    emit('send-patient-data', {activePatients: activePatients.value, inactivePatients: inactivePatients.value});
  } catch (error) {
    console.error("An error occurred while fetching data: ", error);
  }
  emit('toggle-patient-migrate');
};

const displayErrors = (errors) => {
  orgErrors.value = errors;
};

const createOrganization = async () => {
  try {
    emit('saving-updated', true);
    orgErrors.value = {};

    const response = await axios.post('/api/orgs', org.value, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 201) {
      if (org.value.dme_id != null) {
        await axios.post(`/api/orgs/${org.value.dme_id}/migrate`);
        await fetchMigrationPatients(org.value.dme_id);
      }
      org.value = response.data;
      emit('organization-created', response.data);
      emit('saving-updated', false);
    }
  } catch (error) {
    emit('create-organization-error', error);
    if (error.response && error.response.status === 422) {
      displayErrors(error.response.data.errors);
    } else {
      displayErrors(["An unexpected error occurred"]);
    }
  } finally {
    emit('saving-updated', false);
  }
};

defineExpose({
  createOrganization,
  resetOrg,
  activePatients
});

onMounted(async () => {
  try {
    const orgTypesResponse = await axios.get('/api/orgs/orgtypes');
    orgTypes.value = orgTypesResponse.data;

    const orgClinicalTypesResponse = await axios.get('/api/orgs/clinicaltypes');
    orgClinicalTypes.value = orgClinicalTypesResponse.data;

    const equipmentProvider = orgTypes.value.find(orgType => orgType.type === "Equipment Provider");
    if (equipmentProvider) {
      ep_id.value = equipmentProvider.id;
    } else {
      console.warn("Equipment Provider type not found.");
    }

  } catch (error) {
    console.error(error);
  }

  if (props.newLocationData) {
    org.value.dme_id = props.newLocationData.dme_id;
    org.value.name = props.newLocationData.name;
    org.value.type_id = ep_id.value;
    org.value.type = ep_id.value;
    org.value.locations[0].name = props.newLocationData.name;
    org.value.locations[0].address1 = props.newLocationData.address1;
    org.value.locations[0].address2 = 'address2' in props.newLocationData ? props.newLocationData.address2 : '';
    org.value.locations[0].zip = props.newLocationData.zip;
    org.value.locations[0].city = props.newLocationData.city;
    org.value.locations[0].state = props.newLocationData.state;
    org.value.locations[0].country = props.newLocationData.country;
    org.value.locations[0].phone = props.newLocationData.phone;
    if (org.value.dme_id != null) {
      org.value.migrated_by = true;
    }
  }
});
</script>
